import { useId, useMemo } from "react";
import { readPreference } from "../preferences/contracts";
import { effectiveLeadOptions } from "./lead-options";
import type { ReactNode } from "react";
import type { LeadFieldDefinition, LeadFieldValue, Priority } from "../../lib/types";
import { useStore } from "../../lib/store";
import { Field, TextArea, TextInput } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { Combobox } from "../../ui/Combobox";
import { Switch } from "../../ui/Switch";
import { TokenPicker } from "../../ui/TokenPicker";
import {
  formFieldsOf,
  type LeadFormErrors,
  type LeadFormStepId,
  type LeadFormValues,
} from "./form";

/**
 * Renderização da definição única de campos do lead.
 *
 * O componente vive separado de `form.ts` porque um arquivo que exporta
 * componente e função ao mesmo tempo quebra o recarregamento rápido do Vite.
 * Criação e edição usam este mesmo componente, o que é o que garante a
 * equivalência exigida pela matriz do lead (AGENTS §19.1).
 */

const PRIORITY_OPTIONS: Array<{ value: Priority; label: string }> = [
  { value: "low", label: "Baixa" },
  { value: "medium", label: "Média" },
  { value: "high", label: "Alta" },
];

/**
 * Prioridade como controle segmentado, na ordem do protótipo (Baixa, Média,
 * Alta). É um grupo de rádio ARIA: as opções são exclusivas e o teclado navega
 * por setas, como num rádio nativo.
 */
export function PrioritySegments({
  value,
  error,
  onChange,
}: {
  value: Priority;
  error?: string;
  onChange: (value: Priority) => void;
}) {
  const labelId = useId();
  const errorId = useId();
  const move = (delta: number) => {
    const index = PRIORITY_OPTIONS.findIndex((option) => option.value === value);
    const next = PRIORITY_OPTIONS[(index + delta + PRIORITY_OPTIONS.length) % PRIORITY_OPTIONS.length];
    onChange(next.value);
  };
  return (
    <div className="cx-field cx-span-2">
      <span id={labelId} className="cx-field-label" data-required="true">
        Prioridade
      </span>
      <div
        className="choice seg"
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
        onKeyDown={(event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        {PRIORITY_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.value === value}
            tabIndex={option.value === value ? 0 : -1}
            className={option.value === value ? "sel" : undefined}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {error && (
        <span id={errorId} className="cx-field-error">
          {error}
        </span>
      )}
    </div>
  );
}

export interface LeadFormStepProps {
  step: LeadFormStepId;
  funnelId: string;
  values: LeadFormValues;
  errors: LeadFormErrors;
  onChange: (patch: Partial<LeadFormValues>) => void;
  /** Identificador do primeiro campo com erro, para receber foco. */
  focusKey?: string;
  /**
   * Conteúdo inserido logo abaixo de "Função do contato", dentro da grade.
   * É onde o protótipo coloca o atalho de empresa/contato já cadastrados.
   */
  afterRole?: ReactNode;
}

export function LeadFormStep({
  step,
  funnelId,
  values,
  errors,
  onChange,
  focusKey,
  afterRole,
}: LeadFormStepProps) {
  const store = useStore();

  // As sugestões são a lista curada em Configurações > Cadastros somada aos
  // valores em uso que ninguém apagou de propósito (lead-options.ts).
  const leadOptions = useMemo(
    () => readPreference(store.userPreferences ?? [], "leadOptions").value,
    [store.userPreferences],
  );
  const sources = useMemo(
    () => effectiveLeadOptions(leadOptions, store.leads.map((lead) => lead.source), "sources"),
    [leadOptions, store.leads],
  );
  const segments = useMemo(
    () => effectiveLeadOptions(leadOptions, store.leads.map((lead) => lead.segment ?? ""), "segments"),
    [leadOptions, store.leads],
  );
  const tags = useMemo(
    () => store.tags.filter((tag) => tag.funnelId === funnelId && !tag.archived),
    [funnelId, store.tags],
  );
  const triggers = useMemo(
    () => store.triggers.filter((trigger) => trigger.funnelId === funnelId && !trigger.archived),
    [funnelId, store.triggers],
  );
  const fields = useMemo(() => formFieldsOf(store.leadFields, funnelId), [funnelId, store.leadFields]);
  /**
   * Eixos de classificação ativos, com suas opções. Segmento, origem e função
   * têm campo próprio no formulário: repeti-los como eixo pediria o mesmo dado
   * duas vezes, e é a mesma exclusão que o perfil já faz.
   */
  const classifications = useMemo(() => {
    const base = new Set(["segment", "source", "contact_role"]);
    return (store.classificationDefinitions ?? [])
      .filter((definition) => !definition.archived && !base.has(definition.key))
      .sort((left, right) => left.order - right.order)
      .map((definition) => ({
        definition,
        options: (store.classificationOptions ?? [])
          .filter((option) => option.definitionId === definition.id && !option.archived)
          .sort((left, right) => left.order - right.order),
      }))
      .filter((eixo) => eixo.options.length > 0);
  }, [store.classificationDefinitions, store.classificationOptions]);
  const sections = useMemo(
    () =>
      store.formSections
        .filter((section) => section.funnelId === funnelId && !section.archived)
        .sort((a, b) => a.order - b.order),
    [funnelId, store.formSections],
  );

  const autoFocus = (key: string) => (focusKey === key ? true : undefined);

  if (step === "identificacao") {
    return (
      <div className="cx-form-grid">
        <Field label="Nome" required error={errors.name} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              value={values.name}
              placeholder="Ex.: Mariana Duarte"
              autoFocus={autoFocus("name")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => onChange({ name: event.target.value })}
            />
          )}
        </Field>
        <Field label="Empresa" required error={errors.company}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              value={values.company}
              placeholder="Ex.: Clínica Vitalle"
              autoFocus={autoFocus("company")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => onChange({ company: event.target.value })}
            />
          )}
        </Field>
        <Field label="Função do contato" required error={errors.role}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              value={values.role}
              placeholder="Ex.: Sócia-diretora"
              autoFocus={autoFocus("role")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => onChange({ role: event.target.value })}
            />
          )}
        </Field>
        {afterRole}
        <Field label="Segmento" required error={errors.segment}>
          {({ id, describedBy, invalid }) => (
            <Combobox
              id={id}
              suggestions={segments}
              value={values.segment}
              placeholder="Selecionar…"
              describedBy={describedBy}
              invalid={invalid}
              onChange={(value) => onChange({ segment: value })}
            />
          )}
        </Field>
        <Field label="Origem" required error={errors.source}>
          {({ id, describedBy, invalid }) => (
            <Combobox
              id={id}
              suggestions={sources}
              value={values.source}
              placeholder="Selecionar…"
              describedBy={describedBy}
              invalid={invalid}
              onChange={(value) => onChange({ source: value })}
            />
          )}
        </Field>
        <Field label="Valor potencial (R$)" required error={errors.potentialValue}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              inputMode="numeric"
              value={values.potentialValue}
              placeholder="Ex.: 4.500"
              autoFocus={autoFocus("potentialValue")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => onChange({ potentialValue: event.target.value })}
            />
          )}
        </Field>
        <PrioritySegments
          value={values.priority}
          error={errors.priority}
          onChange={(priority) => onChange({ priority })}
        />
      </div>
    );
  }

  if (step === "contato") {
    return (
      <div className="cx-form-grid">
        <Field label="E-mail" error={errors.email}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="email"
              value={values.email}
              autoFocus={autoFocus("email")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => onChange({ email: event.target.value })}
            />
          )}
        </Field>
        <Field label="Telefone" error={errors.phone}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              value={values.phone}
              aria-describedby={describedBy}
              onChange={(event) => onChange({ phone: event.target.value })}
            />
          )}
        </Field>
        <Field label="Instagram" error={errors.instagram} className="cx-span-2">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              value={values.instagram}
              placeholder="@perfil"
              aria-describedby={describedBy}
              onChange={(event) => onChange({ instagram: event.target.value })}
            />
          )}
        </Field>
      </div>
    );
  }

  return (
    <div className="cx-form-grid">
      <TokenPicker
        className="cx-span-2"
        label="Tags"
        options={tags}
        value={values.tagIds}
        onChange={(tagIds) => onChange({ tagIds })}
        onCreate={(name) => store.createTag(name, undefined, funnelId)}
      />
      <TokenPicker
        className="cx-span-2"
        label="Gatilhos"
        options={triggers}
        value={values.triggerIds}
        onChange={(triggerIds) => onChange({ triggerIds })}
        onCreate={(name) => store.createTrigger(name, undefined, funnelId)}
      />
      {classifications.map(({ definition, options }) => (
        <Field label={definition.name} key={definition.id}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              size="field"
              label={definition.name}
              options={[
                { value: "", label: "Não informada" },
                ...options.map((option) => ({ value: option.id, label: option.name })),
              ]}
              value={values.classifications[definition.id] ?? ""}
              placeholder="Não informada"
              describedBy={describedBy}
              onChange={(next) =>
                onChange({
                  classifications: {
                    ...values.classifications,
                    [definition.id]: next ?? "",
                  },
                })
              }
            />
          )}
        </Field>
      ))}
      {sections.map((section) => {
        const sectionFields = fields.filter((field) => field.sectionId === section.id);
        if (!sectionFields.length) return null;
        return (
          <fieldset className="cx-form-section cx-span-2" key={section.id}>
            <legend>{section.name}</legend>
            <div className="cx-form-grid">
              {sectionFields.map((field) => (
                <DynamicField
                  key={field.id}
                  field={field}
                  value={values.fieldValues[field.id]}
                  error={errors[field.id]}
                  focus={focusKey === field.id}
                  onChange={(value) =>
                    onChange({ fieldValues: { ...values.fieldValues, [field.id]: value } })
                  }
                />
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

function DynamicField({
  field,
  value,
  error,
  focus,
  onChange,
}: {
  field: LeadFieldDefinition;
  value: LeadFieldValue["value"];
  error?: string;
  focus?: boolean;
  onChange: (value: LeadFieldValue["value"]) => void;
}) {
  const store = useStore();
  const options = store.leadFieldOptions
    .filter((option) => option.fieldId === field.id && !option.archived)
    .sort((a, b) => a.order - b.order);
  const className = field.width === "full" ? "cx-span-2" : undefined;

  if (field.type === "boolean") {
    return (
      <div className={className}>
        <Switch
          checked={value === true}
          label={field.name}
          hint={field.helpText}
          onChange={(checked) => onChange(checked)}
        />
      </div>
    );
  }

  if (field.type === "multi_select") {
    return (
      <TokenPicker
        className={className}
        label={field.name}
        options={options}
        value={Array.isArray(value) ? value : []}
        help={field.helpText}
        onChange={(ids) => onChange(ids)}
      />
    );
  }

  if (field.type === "select") {
    return (
      <Field label={field.name} required={field.required} error={error} help={field.helpText} className={className}>
        {({ id, describedBy, invalid }) => (
          <Select
            id={id}
            size="field"
            label={field.name}
            options={options.map((option) => ({ value: option.id, label: option.name }))}
            value={typeof value === "string" && value ? value : null}
            placeholder={field.placeholder ?? "Selecionar"}
            describedBy={describedBy}
            invalid={invalid}
            onChange={(next) => onChange(next)}
          />
        )}
      </Field>
    );
  }

  if (field.type === "textarea") {
    return (
      <Field label={field.name} required={field.required} error={error} help={field.helpText} className={className}>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            rows={field.height === "expanded" ? 5 : 3}
            value={typeof value === "string" ? value : ""}
            autoFocus={focus || undefined}
            placeholder={field.placeholder}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </Field>
    );
  }

  const inputType =
    field.type === "date" ? "date" : field.type === "number" || field.type === "currency" ? "text" : "text";

  return (
    <Field label={field.name} required={field.required} error={error} help={field.helpText} className={className}>
      {({ id, describedBy, invalid }) => (
        <TextInput
          id={id}
          type={inputType}
          inputMode={field.type === "number" || field.type === "currency" ? "numeric" : undefined}
          value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
          autoFocus={focus || undefined}
          placeholder={field.placeholder}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}
